import { Component, inject, OnDestroy } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { Subscription } from 'rxjs';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { CommonService } from '../../../../../../../../shared/services/common.service';
import { RiskRegisterService } from '../risk-register.service';
import { RiskImportPreview } from '../risk-register.models';
import { groupSkipped, serverErrorMessage, SkippedGroup } from '../risk-register.util';

/** Import the customer layout: file → dry-run preview → import (spec §4.4, §5.5). */
@Component({
  selector: 'app-risk-import-dialog',
  templateUrl: './risk-import-dialog.component.html',
  styleUrls: ['./risk-import-dialog.component.scss'],
  standalone: false,
})
export class RiskImportDialogComponent implements OnDestroy {
  file: File | null = null;
  sheetName: string | null = null;
  preview: RiskImportPreview | null = null;
  error: string | null = null;
  busy = false;
  skippedGroups: SkippedGroup[] = [];
  /** Backs the shared app-dropdown for the sheet picker. */
  readonly sheetForm = new FormGroup({ sheet: new FormControl<string | null>(null) });
  sheetOptions: { value: string; label: string }[] = [];
  private readonly sheetSub: Subscription = this.sheetForm.controls.sheet.valueChanges.subscribe(v => {
    if (v && v !== this.sheetName) this.onSheet(v);
  });
  readonly maxBytes = 10 * 1024 * 1024;

  readonly dialogRef = inject<MatDialogRef<RiskImportDialogComponent>>(MatDialogRef);
  readonly data = inject<{ projectId: string }>(MAT_DIALOG_DATA);
  private readonly riskService = inject(RiskRegisterService);
  private readonly commonService = inject(CommonService);

  onFile(event: Event): void {
    const input = event.target as HTMLInputElement;
    const f: File | undefined = input.files?.[0];
    input.value = '';
    if (!f) return;
    if (!/\.xlsx$/i.test(f.name)) { this.reject('Please choose an .xlsx file.'); return; }
    if (f.size > this.maxBytes) { this.reject('The file is larger than 10 MB.'); return; }
    this.file = f; this.error = null; this.preview = null; this.sheetName = null;
    this.runPreview();
  }

  runPreview(): void {
    if (!this.file || this.busy) return;
    this.busy = true;
    this.riskService.importPreview(this.data.projectId, this.file, this.sheetName || undefined).subscribe({
      next: p => {
        this.busy = false; this.preview = p; this.sheetName = p.sheet_name;
        this.skippedGroups = groupSkipped(p.skipped);
        this.sheetOptions = p.sheet_names.map(n => ({ value: n, label: n }));
        this.sheetForm.controls.sheet.setValue(p.sheet_name, { emitEvent: false });
      },
      error: err => { this.busy = false; this.preview = null; this.error = serverErrorMessage(err?.error, 'Could not read the spreadsheet'); },
    });
  }

  private reject(message: string): void {
    this.file = null; this.preview = null; this.sheetName = null; this.skippedGroups = []; this.error = message;
  }

  ngOnDestroy(): void { this.sheetSub.unsubscribe(); }

  onSheet(name: string): void { this.sheetName = name; this.runPreview(); }

  import(): void {
    if (!this.file || !this.preview || this.busy) return;
    this.busy = true;
    this.riskService.import(this.data.projectId, this.file, this.sheetName || undefined).subscribe({
      next: p => { this.busy = false; this.commonService.notification(`${p.imported} risk${p.imported === 1 ? '' : 's'} imported`, 'success'); this.dialogRef.close(true); },
      error: err => { this.busy = false; this.error = serverErrorMessage(err?.error, 'Import failed'); },
    });
  }
}
